import { ERROR_UNAUTHORIZED } from "./constants";

const API_KEY = 'fea5da2f96ac35b6c47f2c777e8a4d9c59fea3cb12a0f1dfbcf6f5768ec772a7';

/**
 * API abstracts the interaction with the backend.
 *
 * @class
 */
export class Api {
  /**
   * @param {Object} parameters
   * @param {string} parameters.baseUrl - The API base URL.
   */
  constructor({ baseUrl = "/", accessToken = "" } = {}) {
    this.baseUrl = baseUrl;
    this.accessToken = accessToken;
  }

  /**
   * Fetch wrapper to fill in common configuration.
   *
   * @param {string} resource - Defines the resource that you wish to fetch.
   * @param {Object} options - Object containing any custom settings you want to apply to the request.
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  fetch(resource, options) {
    const headers = {
      "content-type": "application/json",
      ...(this.accessToken && { authorization: `Bearer ${this.accessToken}` }),
      ...options?.headers,
    };
    return fetch(`${this.baseUrl}${resource}`, {
      ...options,
      headers,
    })
    .then(async res => {
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.errors?.join?.(" ") || json?.error || res?.statusText);
      }
      return res;
    })
    .then(res => res.json())
    .then(json => {
      if (json?.errors || json?.error) {
        throw new Error(json?.errors?.join?.(" ") || json?.error);
      }
      return json;
    });
  }

  /**
   * Exchange credentials for tokens (aka login).
   *
   * @param {Object} parameters
   * @param {string} parameters.email - Email address.
   * @param {string} parameters.password - Password.
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  exchangeCredentialsForTokens({ email, password }) {
    return this.fetch("v1/auth/token", {
      body: JSON.stringify({
        email,
        password,
        grant_type: "password",
        app_type: "v1-web-app",
      }),
      method: "POST",
    });
  }

  /**
   * Retrieve logged in user. Requires an `accessToken` to have been supplied.
   *
   * @throws {ERROR_UNAUTHORIZED}
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  retrieveLoggedInUser() {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch("v1/users/authenticated_user");
  }

  /**
   * Finds courses by title.
   * Requires an `accessToken` to have been supplied.
   *
   * @param {Object} parameters
   * @param {string} parameters.title - The course title to search for.
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  findCourses({ title = "" } = {}) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch(`v2/bookings/find_courses?title=${title}`);
  }

  /**
   * Creates a booking request via the Client API.
   */
  createClientBookingRequest({
      client_id,
      course_id,
      date,
      location_address,
      location_latitude,
      location_longitude,
      remote_facilitated,
      notes,
      timing,
      po_number,
      requestor_id,
      booking_contact_id,
  }) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch(`client/v1/booking_requests`, {
      body: JSON.stringify({
        booking_request: {
          client_id,
          course_id,
          date,
          location_address,
          location_latitude,
          location_longitude,
          remote_facilitated,
          notes,
          timing,
          po_number,
          requestor_id,
          booking_contact_id,
        }
      }),
      method: "POST",
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Fetch booking requests.
   */
  fetchBookingRequests() {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch('client/v1/booking_requests', {
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Fetch booking request.
   */
  fetchBookingRequest({ id }) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch(`v3/booking_requests/${id}`, {
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Retrieve a single booking request from the Client API.
   */
  fetchClientBookingRequest({ id }) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch(`client/v1/booking_requests/${id}`, {
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Select an alternative date for a booking request on the Client API.
   */
  selectBookingRequestAlternativeDate({ id, date }) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch(`client/v1/booking_requests/${id}/select_alternative_date`, {
      body: JSON.stringify({ date }),
      method: "POST",
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Fetch client courses.
   */
  fetchClientCourses() {
    return this.fetch('public/v1/courses', {
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Fetch course.
   */
  fetchCourse({ id }) {
    return this.fetch(`public/v1/courses/${id}`, {
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Create checkout session.
   */
  createCheckoutSession(body) {
    return this.fetch('v3/checkout_sessions', {
      headers: {
        "X-API-Key": API_KEY,
      },
      body: JSON.stringify(body),
      method: "POST",
    })
  }

  /**
   * Login.
   *
   * @param {Object} parameters
   * @param {string} parameters.email - Email address
   * @param {string} parameters.password - The password.
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  login({ email, password }) {
    return this.fetch("v3/auth/login", {
      body: JSON.stringify({
        email,
        password,
      }),
      credentials: "include",
      method: "POST",
    });
  }

  /**
   * Fetch checkout session.
   */
  fetchCheckoutSession({ key }) {
    return this.fetch(`v3/checkout_sessions/${key}`, {
      headers: {
        "X-API-Key": API_KEY,
      }
    });
  }

  /**
   * Logout.
   *
   * @param {Object} parameters
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  logout() {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch("v3/auth/logout", {
      method: "DELETE",
      credentials: "include",
    });
  }

  /**
   * Create SSO session.
   *
   * @param {Object} parameters
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  createSsoSession() {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch("v3/auth/sso_session", {
      method: "POST",
      credentials: "include",
    });
  }

  /**
   * Retrieve SSO session.
   *
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  retrieveSsoToken() {
    // Uses normal default browser `fetch`!
    return fetch(`${this.baseUrl}v3/auth/sso_token`, {
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
    });
  }

  /**
   * Fetch requestors. Requires an `accessToken` to have been supplied.
   *
   * @param {Object} parameters
   * @param {string} parameters.name - Filter requestors by name (searches in firstname and surname).
   * @throws {ERROR_UNAUTHORIZED}
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  fetchRequestors({ name = "" } = {}) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    const params = new URLSearchParams();
    if (name) {
      params.set("name", name);
    }
    const query = params.toString();
    return this.fetch(`v3/requestors${query ? `?${query}` : ""}`);
  }

  /**
   * Create a requestor. Requires an `accessToken` to have been supplied.
   *
   * @param {Object} parameters
   * @param {string} parameters.firstname - The requestor's first name.
   * @param {string} parameters.surname - The requestor's surname.
   * @param {string} parameters.tel - The requestor's telephone number.
   * @param {string} parameters.email - The requestor's email address.
   * @throws {ERROR_UNAUTHORIZED}
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  createRequestor({ firstname, surname, tel, email }) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch("v3/requestors", {
      body: JSON.stringify({
        requestor: {
          firstname,
          surname,
          tel,
          email,
        },
      }),
      method: "POST",
    });
  }

  /**
   * Fetch requestors from the Client API. Requires an `accessToken` to have been supplied.
   *
   * @param {Object} parameters
   * @param {string} parameters.name - Case-insensitive partial match on firstname, surname, or full name.
   * @throws {ERROR_UNAUTHORIZED}
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  fetchClientRequestors({ name = "" } = {}) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    const params = new URLSearchParams();
    if (name) {
      params.set("name", name);
    }
    const query = params.toString();
    return this.fetch(`client/v1/requestors${query ? `?${query}` : ""}`, {
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Create a requestor via the Client API. Requires an `accessToken` to have been supplied.
   *
   * @param {Object} parameters
   * @param {string} parameters.firstname - The requestor's first name.
   * @param {string} parameters.surname - The requestor's surname.
   * @param {string} parameters.tel - The requestor's telephone number.
   * @param {string} parameters.email - The requestor's email address.
   * @throws {ERROR_UNAUTHORIZED}
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  createClientRequestor({ firstname, surname, tel, email }) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch("client/v1/requestors", {
      body: JSON.stringify({
        requestor: {
          firstname,
          surname,
          tel,
          email,
        },
      }),
      method: "POST",
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Fetch booking contacts from the Client API. Requires an `accessToken` to have been supplied.
   *
   * @param {Object} parameters
   * @param {string} parameters.name - Case-insensitive partial match on firstname, surname, or full name.
   * @throws {ERROR_UNAUTHORIZED}
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  fetchClientContacts({ name = "" } = {}) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    const params = new URLSearchParams();
    if (name) {
      params.set("name", name);
    }
    const query = params.toString();
    return this.fetch(`client/v1/booking_contacts${query ? `?${query}` : ""}`, {
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   * Create a booking contact via the Client API. Requires an `accessToken` to have been supplied.
   *
   * @param {Object} parameters
   * @param {string} parameters.firstname - The contact's first name.
   * @param {string} parameters.surname - The contact's surname.
   * @param {string} parameters.tel - The contact's telephone number.
   * @param {string} parameters.email - The contact's email address.
   * @throws {ERROR_UNAUTHORIZED}
   * @returns{Promise<Response>} A Promise that resolves to a Response object.
   */
  createClientContact({ firstname, surname, tel, email }) {
    if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    return this.fetch("client/v1/booking_contacts", {
      body: JSON.stringify({
        booking_contact: {
          firstname,
          surname,
          tel,
          email,
        },
      }),
      method: "POST",
      headers: {
        "X-API-Key": API_KEY,
      },
    });
  }

  /**
   *
   */
  retrieveEventsCalendar({ month = "", year = "" } = {}) {
      if (!this.accessToken) {
      throw new Error(ERROR_UNAUTHORIZED);
    }
    const params = new URLSearchParams({
      month,
      year,
    });
    return this.fetch(`v3/events/calendar?${params}`);
  }
}
